// src/features/system/admin/components/UserManagement.tsx
import React from 'react';
import { useAuth } from '@/shared/auth';
import UsersTable from '@/features/system/user/components/UsersTable';

/**
 * UserManagement – hosts all user administration UI (previously the Users sub-tab of SystemManagement).
 * Shows the users table if the viewer has either Std or Full user info permission; otherwise a denial message.
 */
export default function UserManagement() {
  const auth = useAuth();
  const { loading, roles, perms, systemTabs } = auth;

  // Normalize perms to array for includes checks
  const permList = Array.from(perms.values());
  const has = (p: string) => permList.includes(p);

  // Multiple fallback avenues for access so we don't lock out legitimate admins:
  const serverTabAllows = systemTabs?.some(t => t.key === 'users');
  const permAllows = has('system.Users.StdUserInfo') || has('system.Users.FullUserInfo');
  const roleAllows = roles?.includes('superadmin');

  // If backend sent NO perms at all but also no explicit denial, be permissive.
  const emptyPermsFallback = permList.length === 0;

  const canSeeUsers = serverTabAllows || permAllows || roleAllows || emptyPermsFallback;

  if (loading) {
    return <section className="bg-neutral-900 rounded-lg p-4">Loading…</section>;
  }

  if (!canSeeUsers) {
    return (
      <section className="bg-neutral-900 rounded-lg p-4">
        <p className="text-neutral-400">You don’t have permission to view users.</p>
        <p className="text-neutral-500 text-xs mt-2">(Need system.Users.StdUserInfo / FullUserInfo, users tab in systemTabs, or superadmin role.)</p>
      </section>
    );
  }

  return (
    <section className="bg-neutral-900 rounded-lg p-4 space-y-4">
      <h2 className="text-xl font-semibold">Users</h2>
      <UsersTable />
    </section>
  );
}
